import React from 'react';
import { View, StyleSheet } from 'react-native';
import { Banner, useTheme, Text, Button } from 'react-native-paper';
import { useTranslation } from 'react-i18next';

interface PhoneVerificationBannerProps {
  /** Whether phone is verified */
  isVerified: boolean;
  /** Whether phone number exists */
  hasPhone: boolean;
  /** Callback when user presses verify CTA */
  onVerifyPress?: () => void;
  /** Callback when user dismisses banner */
  onDismiss?: () => void;
  /** Test ID for testing */
  testID?: string;
}

export const PhoneVerificationBanner: React.FC<PhoneVerificationBannerProps> = ({
  isVerified,
  hasPhone,
  onVerifyPress,
  onDismiss,
  testID = 'phone-verification-banner',
}) => {
  const { t } = useTranslation();
  const theme = useTheme();

  // Don't render if verified or no phone number
  if (isVerified || !hasPhone) {
    return null;
  }

  return (
    <Banner
      visible={true}
      style={[styles.banner, { backgroundColor: theme.colors.errorContainer }]}
      testID={testID}
      actions={[
        {
          label: t('verify', 'Verify'),
          onPress: onVerifyPress || (() => {}),
          testID: `${testID}-action`,
        },
        ...(onDismiss ? [{
          label: t('dismiss', 'Dismiss'),
          onPress: onDismiss,
          testID: `${testID}-dismiss`,
        }] : []),
      ]}
      icon={({ size }) => (
        <View style={[styles.iconContainer, { backgroundColor: theme.colors.error }]}>
          <Text style={{ color: theme.colors.onError, fontSize: size * 0.7 }}>
            !
          </Text>
        </View>
      )}
    >
      <View style={styles.content}>
        <Text variant="titleSmall" style={[styles.title, { color: theme.colors.onErrorContainer }]}>
          {t('phoneNotVerified', 'Phone Number Not Verified')}
        </Text>
        <Text variant="bodySmall" style={{ color: theme.colors.onErrorContainer }}>
          {t('phoneVerificationRequired', 'Verify your phone number to enable all features.')}
        </Text>
      </View>
    </Banner>
  );
};

const styles = StyleSheet.create({
  banner: {
    marginHorizontal: 16,
    marginVertical: 8,
    borderRadius: 12,
  },
  content: {
    flex: 1,
    paddingRight: 8,
  },
  title: {
    fontWeight: '600',
    marginBottom: 2,
  },
  iconContainer: {
    width: 24,
    height: 24,
    borderRadius: 12,
    alignItems: 'center',
    justifyContent: 'center',
  },
});

export default PhoneVerificationBanner;
